<template>
    <b-modal v-model="visibleComputed" size="xxl" centered :title="$t('map.sendToPhone.title')" body-class="f-14" @show="setScreen(`home`)">
        <template v-if="screen === 'home'">
            <div class="text-center mb-4">
                <b-img :src="require('@/assets/icons/wayfinding/send-to-phone.webp')" width="150"/>
                <h2>{{ $t('map.sendToPhone.heading') }}</h2>
            </div>
            <b-row>
                <b-col class="text-center">
                    <div class="p1 mb-2">{{ $t('map.sendToPhone.scanQrCode') }}</div>
                    <qr-code-with-overlay class="mx-auto" :url="`${baseSharingUrl}&medium=kiosk-to-phone&source=qr-code`"/>
                    <span v-if="isLocal()">{{`${baseSharingUrl}&medium=kiosk-to-phone&source=qr-code`}}</span>
                    <div class="p3 my-3">
                        {{ $t('map.sendToPhone.scanInstructions') }}
                    </div>
                </b-col>
                <b-col>
                    <b-card class="gradient-card mt-5" @click="setScreen('sms')" no-body style="height: 250px;">
                        <b-card-body>
                            <b-img width="150" :src="require('@/assets/icons/sms-code.svg')"/>
                            <div class="ml-2 my-2 pl-2 text-left">
                                <div class="p1 mb-2 text-accent">{{ $t('map.sendToPhone.sendBySms') }}</div>
                                <div>{{ $t('map.sendToPhone.noPhoneStorage') }}</div>
                            </div>
                        </b-card-body>
                    </b-card>
                </b-col>
            </b-row>
            <hr>
            <div class="text-center">
                <h2 class="mt-4 mb-4">{{ $t('map.sendToPhone.howToScanQr') }}</h2>
            </div>
            <b-row>
                <b-col>
                    <b-card class="gradient-card" @click="setScreen('iphone')" style="height: 250px;">
                        <i class="fab fa-apple fa-6x" style="color: #777;"/>
                        <div class="ml-2 my-2 pl-2 text-left">
                            <div class="p1 mb-2 text-accent">{{ $t('map.sendToPhone.withIphone') }}</div>
                            <div>{{ $t('map.sendToPhone.iphoneInstructions') }}</div>
                        </div>
                    </b-card>
                </b-col>
                <b-col>
                    <b-card class="gradient-card" @click="setScreen('android')" no-body style="height: 250px;">
                        <b-card-body style="border-color: #4faf53">
                            <i class="fab fa-android fa-6x" style="color: #4faf53;"/>
                            <div class="ml-2 my-2 pl-2 text-left">
                                <div class="p1 mb-2 text-success">{{ $t('map.sendToPhone.withAndroid') }}</div>
                                <div>{{ $t('map.sendToPhone.androidInstructions') }}</div>
                            </div>
                        </b-card-body>
                    </b-card>
                </b-col>
            </b-row>
        </template>

        <template v-if="screen === 'sms'">
            <b-btn @click="setScreen('home')" variant="info" size="lg" style="position: absolute; left: 10px; top: 10px;">
                <i class="fa-chevron-left mr-1" :class="fontAwesomePrefix"/>
                <span>{{ $t('map.sendToPhone.back') }}</span>
            </b-btn>
            <h1 class="mb-3 text-center">{{ $t('map.sendToPhone.sendByText') }}</h1>

            <div class="text-center">
                <div class="p2 text-center">{{ $t('map.sendToPhone.enterPhoneNumber') }}</div>
                <b-input-group class="mt-2 mb-3 mx-auto" size="lg" style="width: 400px">
                    <b-select v-model="extension" style="max-width: 140px">
                        <option v-for="(phone, isoCode) in countryPhones" :value="phone">
                            {{ isoCode }} {{ phone }}
                        </option>
                    </b-select>
                    <b-input v-model="phone" size="lg" maxlength="10" autofocus placeholder="(___) ___-____"/>
                </b-input-group>

                <virtual-keypad :value="phone" @change="onKeyboardChange" :max-length="10"/>

                <b-btn @click="shareSMS" block class="mt-3 mx-auto" size="lg" variant="accent" :disabled="!isPhoneValid" style="width: 400px; border: 2px solid rgba(0, 0, 0, 0.3)">
                    <span>{{ $t('map.sendToPhone.send') }}</span>
                    <i class="fas fa-paper-plane-top ml-1"/>
                </b-btn>

                <div class="mt-4 mb-3">{{ $t('map.sendToPhone.noPhoneStorage') }}</div>
            </div>
        </template>

        <template v-if="screen === 'iphone'">
            <b-btn @click="setScreen('home')" variant="info" size="lg" style="position: absolute; left: 10px; top: 10px;">
                <i class="fa-chevron-left mr-1" :class="fontAwesomePrefix"/>
                <span>{{ $t('map.sendToPhone.back') }}</span>
            </b-btn>
            <h1 class="mb-3 text-center">{{ $t('map.sendToPhone.withIphone') }}</h1>
            <b-row>
                <b-col cols="4" class="text-center">
                    <div class="d-flex align-items-center" style="height: 500px">
                        <b-img class="mx-auto" width="150" :src="require('@/assets/img/qr-codes/iphone-photo-icon.webp')"/>
                    </div>
                    <p class="p2 mt-2">{{ $t('map.sendToPhone.openCameraApp') }}</p>
                </b-col>
                <b-col cols="4" class="text-center">
                    <b-img height="500" :src="require('@/assets/img/qr-codes/qr-scan-hand.webp')"/>

                    <p class="p2 mt-2">{{ $t('map.sendToPhone.placeCameraForCode') }}</p>
                </b-col>
                <b-col cols="4" class="text-center">
                    <qr-code-with-overlay class="mx-auto" height="500" center :url="`${baseSharingUrl}&medium=kiosk-to-phone&source=qr-code`" :overlay-url="require('@/assets/img/qr-codes/iphone-qrcode-overlay-tuto.webp')" />
                    <p class="p2 mt-2">{{ $t('map.sendToPhone.tapYellowPopup') }}</p>
                </b-col>
            </b-row>
        </template>

        <template v-if="screen === 'android'">
            <b-btn @click="setScreen('home')" variant="info" size="lg" style="position: absolute; left: 10px; top: 10px;">
                <i class="fa-chevron-left mr-1" :class="fontAwesomePrefix"/>
                <span>{{ $t('map.sendToPhone.back') }}</span>
            </b-btn>
            <h1 class="mb-3 text-center">{{ $t('map.sendToPhone.withAndroid') }}</h1>
            <b-row>
                <b-col cols="4" class="text-center">
                    <div class="d-flex align-items-center" style="height: 180px">
                        <b-img class="mx-auto" width="150" :src="require('@/assets/img/qr-codes/android-google-icon.webp')"/>
                    </div>
                    <p class="p2 mt-2">{{ $t('map.sendToPhone.openGoogleApp') }}</p>
                </b-col>
                <b-col cols="4" class="text-center">
                    <b-img height="180" :src="require('@/assets/img/qr-codes/google-app-photo.webp')"/>

                    <p class="p2 mt-2">{{ $t('map.sendToPhone.tapCameraIcon') }}</p>
                </b-col>
                <b-col cols="4" class="text-center">
                    <b-img width="300" class="mb-3" :src="require('@/assets/img/qr-codes/google-app-search.webp')"/>

                    <p class="p2 mt-2">{{ $t('map.sendToPhone.tapSearchWithCamera') }}</p>
                </b-col>
            </b-row>
            <b-row class="mt-2">
                <b-col cols="4" class="text-center ml-auto">
                    <b-img height="500" :src="require('@/assets/img/qr-codes/qr-scan-hand.webp')"/>

                    <p class="p2 mt-2">{{ $t('map.sendToPhone.placeCameraForCode') }}</p>
                </b-col>
                <b-col cols="4" class="text-center mr-auto">
                    <qr-code-with-overlay class="mx-auto" height="500" center :url="`${baseSharingUrl}&medium=kiosk-to-phone&source=qr-code`" :overlay-url="require('@/assets/img/qr-codes/android-qrcode-overlay-tuto.webp')" />
                    <p class="p2 mt-2">{{ $t('map.sendToPhone.tapWebsiteButton') }}</p>
                </b-col>
            </b-row>
        </template>

        <template #modal-footer>
            <b-btn variant="" @click="visibleComputed = false">
                <span>{{ $t('map.sendToPhone.close') }}</span>
            </b-btn>
        </template>
    </b-modal>
</template>

<script>
import QrCodeWithOverlay from "@/components/qr-code-with-overlay.vue";
import VirtualKeyboard from "@/components/virtual-keyboard.vue";
import VirtualKeypad from "@/components/virtual-keypad.vue";
import SessionTracker from "@/helpers/SessionTracker.js";
import EQrCode from "@vue-components/components/e-qr-code.vue";

import countryPhones from "@vue-components/nuxt/components/ui/country-phones.json"
import NetworkV2 from "@vue-components/helpers/NetworkV2.js";
import Validation from "@vue-components/helpers/Validation.js";

// modal-send-to-phone
export default {
    name: `modal-send-to-phone`,
    components: {EQrCode, VirtualKeyboard, VirtualKeypad, QrCodeWithOverlay},
    props: {
        baseSharingUrl: {type: String},
        visible: {type: Boolean},
        navPath: { type: Object },
        analyticsRoute: {type: Object, required: true},
    },
    data() {
        return {
            extension: `+1`,
            screen: `home`,// home|
            device: `iphone`,// iphone|android
            ext: ``,
            phone: ``,
            countryPhones
        }
    },
    computed: {
        visibleComputed: {
            get() {
                return this.visible;
            },
            set(value) {
                this.$emit(`update:visible`, value);
            }
        },
        isPhoneValid() {
            return Validation.testPhoneNumber(this.extension + this.phone, true);
        }
    },
    methods: {
        onKeyboardChange(input) {
            if (input && input.length > 10) {
                input = input.substr(0, 10);
            }
            this.phone = input;
        },
        setScreen(screen) {
            this.screen = screen;
        },
        close() {
            this.setScreen(`home`);
            this.visibleComputed = false;
        },
        onHidden() {
            this.setScreen(`home`);
        },
        shareSMS() {
            this.showLoading();

            NetworkV2.post(`/api/public/share/nav-path/sms`, {
                language: this.language,
                analytics: this.analyticsRoute,
                destination: this.navPath.end.getName(),
                phone: this.phone,
                extension: this.extension,
                url: `${this.baseSharingUrl}&medium=kiosk-to-phone&source=sms`
            })
                .then(_ => {
                    this.close();
                    this.setScreen(`home`);
                    this.phone = ``;
                    this.showSavedMessage(this.$t(`map.sendToPhone.smsSent`), this.$t(`map.sendToPhone.checkSmsMessage`));
                })
                .catch(e => {
                    console.error(e);
                    this.showErrorMessage(this.$t(`map.sendToPhone.invalidNumber`), this.$t(`map.sendToPhone.checkPhoneNumber`));
                });
        }
        // formatPhone(phone) {
        //     // let value = `(___) ___ ____`;
        //
        //     let value = phone.replace(/\D/g, ``).match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
        //     value = !value[2] ? `(` + value[1] : `(` + value[1] + `) ` + value[2] + (value[3] ? `-` + value[3] : ``);
        //
        //     value = value + `(___) ___ ____`.substring(value.length);
        //
        //     return value;
        // }
    }
}
</script>

<style lang="scss">
.gradient-card {
    .card-body {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-start;
        border: 2px solid var(--color-accent);
        background: linear-gradient(180deg, #ffffff, #e3f3f2);
        border-radius: inherit
    }
}
</style>
